import { ChevronDown, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils/common";

export const GROUP_THRESHOLD = 5;

export type FilterBadgeItem = {
  readonly value: string;
  readonly label: string;
  readonly active: boolean;
  /**
   * Colour classes for the leading dot, as a `bg-*` and a matching `ring-*`
   * so the inactive (hollow) state can keep the colour while dropping the
   * fill. Both are needed because Tailwind cannot derive one from the other.
   * Optional: only filters whose values carry a colour of their own use it.
   */
  readonly dotClassName?: string;
};

/**
 * The `×` that removes a value, or a whole group. It sits as a *sibling* of the
 * label control rather than inside it: a button nested in a button is invalid
 * HTML, and working around that by hanging the click on the badge `<div>` is
 * what previously left the pills unreachable by keyboard.
 */
function RemoveButton({ accessibleLabel, onRemove }: { accessibleLabel: string; onRemove: () => void }) {
  return (
    <button
      type="button"
      className="ring-offset-background focus-visible:ring-ring ml-1 rounded-full text-gray-700 outline-none transition-colors hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-offset-2"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove();
      }}
      aria-label={accessibleLabel}
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

/**
 * Purely decorative: the status text is always present alongside it, so the
 * colour reinforces rather than carries the meaning.
 */
function FilterBadgeDot({ active, dotClassName }: { active: boolean; dotClassName: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("mr-1.5 size-2 shrink-0 rounded-full", dotClassName, !active && "bg-transparent ring-1 ring-inset")}
    />
  );
}

export function FilterBadge({
  label,
  labelPrefix,
  dotClassName,
  onRemove,
  active,
  onToggle,
}: {
  label: string;
  /**
   * Rendered in front of the label in a muted tone, so "Source: Hy-Plains
   * Feedyard" reads as a value belonging to a filter rather than as one long
   * string. Folded back into the test id and accessible name so those stay
   * the full, human-readable phrase.
   */
  labelPrefix?: string;
  dotClassName?: string;
  onRemove: () => void;
  active: boolean;
  onToggle?: () => void;
}) {
  const fullLabel = labelPrefix ? `${labelPrefix}${label}` : label;

  const content = (
    <>
      {dotClassName ? <FilterBadgeDot active={active} dotClassName={dotClassName} /> : null}
      {labelPrefix ? <span className="text-muted-foreground mr-1 font-normal">{labelPrefix}</span> : null}
      {label}
    </>
  );

  return (
    <Badge
      data-testid={`badge-${fullLabel.toLowerCase()}`}
      variant="filter"
      className={!active ? "text-muted-foreground" : ""}
    >
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={active}
          className="ring-offset-background focus-visible:ring-ring inline-flex cursor-pointer items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {content}
        </button>
      ) : (
        <span className="inline-flex items-center">{content}</span>
      )}
      <RemoveButton accessibleLabel={`Remove ${fullLabel}`} onRemove={onRemove} />
    </Badge>
  );
}

type GroupedFilterBadgeProps = {
  groupLabel: string;
  items: readonly FilterBadgeItem[];
  onRemove: (value: string) => void;
  onToggle: (value: string) => void;
  onRemoveAll: () => void;
};

export function GroupedFilterBadge({ groupLabel, items, onRemove, onToggle, onRemoveAll }: GroupedFilterBadgeProps) {
  const [open, setOpen] = useState(false);

  const activeCount = items.filter((item) => item.active).length;
  const label = `${groupLabel} - ${activeCount} selected`;

  const handleRemoveAll = () => {
    onRemoveAll();
    setOpen(false);
  };

  const handleRemove = (value: string) => {
    onRemove(value);
    if (items.length === 1) {
      setOpen(false);
    }
  };

  // No dimmed state for a partial selection, and no dot: the count already
  // says how many of the group are on, and a row of mini-dots for a set is
  // noise rather than information.
  return (
    <Badge variant="filter">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`badge-grouped-${groupLabel.toLowerCase()}`}
            className="ring-offset-background focus-visible:ring-ring inline-flex cursor-pointer items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {label}
            <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 max-h-[400px] overflow-y-auto" align="start">
          <ScrollArea className="h-full">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{groupLabel}</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveAll}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </Button>
              </div>
              <Separator />
              <div className="flex flex-wrap gap-1">
                {items.map(({ value, label, active, dotClassName }) => (
                  <FilterBadge
                    key={value}
                    label={label}
                    dotClassName={dotClassName}
                    onRemove={() => handleRemove(value)}
                    onToggle={() => onToggle(value)}
                    active={active}
                  />
                ))}
              </div>
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
      <RemoveButton accessibleLabel={`Remove all ${groupLabel} filters`} onRemove={handleRemoveAll} />
    </Badge>
  );
}

/**
 * Renders one filter group the way its size warrants: individual pills while
 * the group is small enough to read at a glance, a single collapsed pill once
 * it is not.
 *
 * `labelPrefix` applies only to the individual pills, which stand alone in the
 * badge row and would otherwise not say which filter they belong to. Inside the
 * grouped popover the heading already carries the group name, so prefixing
 * there would just repeat it.
 */
export function FilterBadgeGroup({
  groupLabel,
  items,
  onRemove,
  onToggle,
  onRemoveAll,
  labelPrefix,
}: {
  groupLabel: string;
  items: readonly FilterBadgeItem[];
  onRemove: (value: string) => void;
  onToggle: (value: string) => void;
  onRemoveAll: () => void;
  labelPrefix?: string;
}) {
  if (items.length === 0) return null;

  if (items.length > GROUP_THRESHOLD) {
    return (
      <GroupedFilterBadge
        groupLabel={groupLabel}
        items={items}
        onRemove={onRemove}
        onToggle={onToggle}
        onRemoveAll={onRemoveAll}
      />
    );
  }

  return (
    <>
      {items.map(({ value, label, active, dotClassName }) => (
        <FilterBadge
          key={value}
          active={active}
          label={label}
          labelPrefix={labelPrefix}
          dotClassName={dotClassName}
          onRemove={() => onRemove(value)}
          onToggle={() => onToggle(value)}
        />
      ))}
    </>
  );
}
